import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Mail, RefreshCw, Send, UserPlus, X } from 'lucide-react'
import { useId, useRef, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'

import { Button } from '../../ui/Button/Button'
import { Skeleton } from '../../ui/Skeleton/Skeleton'
import { StatusBadge } from '../../ui/StatusBadge/StatusBadge'
import { useAuth } from '../auth/AuthProvider'
import {
  getOnboardingApiClient,
  type OnboardingApiClient,
} from '../onboarding/onboarding-api'
import type {
  AdministratorInvitation,
  OrganizationResponse,
} from '../onboarding/organization-onboarding-contracts'
import { usePatientDialog } from '../patients/use-patient-dialog'
import {
  administratorLabel,
  cancelInvitationError,
  emailProblem,
  initials,
  invitationError,
  invitationStatus,
  openInvitations,
} from './organization-model'
import { administratorAccessQuery } from './organization-queries'
import { SettingsConfirmDialog } from './SettingsConfirmDialog'
import { canManageOrganization } from './settings-access'
import panel from './SettingsSection.module.css'
import styles from './OrganizationSettings.module.css'

export function OrganizationAdministrators({
  organization,
  apiClient,
  notify,
}: {
  organization: OrganizationResponse
  apiClient?: OnboardingApiClient
  notify?: (kind: 'success' | 'error', message: string) => void
}) {
  const auth = useAuth()
  const queryClient = useQueryClient()
  const user = auth.status === 'authenticated' ? auth.user : null
  const api = apiClient ?? getOnboardingApiClient()
  const organizationId = organization.organization.id
  const authorized =
    user !== null &&
    canManageOrganization(organization) &&
    !!api.getAdministratorAccess
  const [inviting, setInviting] = useState(false)
  const [cancelling, setCancelling] = useState<AdministratorInvitation | null>(
    null
  )
  const query = administratorAccessQuery(api, user, organizationId)
  const access = useQuery({ ...query, enabled: authorized })
  const accessMismatch =
    access.data !== undefined && access.data.organizationId !== organizationId
  const accessVerified = authorized && access.isSuccess && !accessMismatch
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: query.queryKey })
  const resend = useMutation({
    mutationFn: (email: string) =>
      api.createAdministratorInvitation!(user!, { email }),
    onSuccess: async (_result, email) => {
      notify?.('success', `Invitation resent to ${email}.`)
      await refresh()
    },
    onError: (error) => notify?.('error', invitationError(error)),
  })
  const revoke = useMutation({
    mutationFn: (invitation: AdministratorInvitation) =>
      api.revokeAdministratorInvitation!(user!, invitation.invitationId),
    onSuccess: async (_result, invitation) => {
      setCancelling(null)
      notify?.('success', `Invitation for ${invitation.email} cancelled.`)
      await refresh()
    },
  })

  if (!authorized) return null

  const administrators = accessVerified
    ? (access.data?.administrators ?? [])
    : []
  const pending = accessVerified
    ? openInvitations(access.data?.invitations ?? [])
    : []

  return (
    <section
      className={panel.panel}
      aria-labelledby="organization-administrators-title"
    >
      <div className={panel.panelHeader}>
        <div className={panel.heading}>
          <span className={panel.eyebrow}>Administrators</span>
          <h2 id="organization-administrators-title">
            Organization administrators
          </h2>
          <p>
            Full access to this organization, every clinic and its managers.
          </p>
        </div>
        <div className={panel.headerActions}>
          <Button
            type="button"
            variant="secondary"
            disabled={!accessVerified}
            onClick={() => setInviting(true)}
          >
            <UserPlus size={16} aria-hidden="true" /> Invite administrator
          </Button>
        </div>
      </div>

      {access.isPending ? (
        <div className={styles.peoplePanelBody} role="status">
          <span className={panel.srOnly}>Loading administrators</span>
          <Skeleton height="2.75rem" />
          <Skeleton height="2.75rem" width="70%" />
        </div>
      ) : !accessVerified ? (
        <div className={panel.notice} data-tone="warning" role="alert">
          <AlertTriangle size={16} aria-hidden="true" />
          <div>
            <strong>Administrators couldn’t be loaded safely</strong>
            <p>Nothing has changed. Try loading them again.</p>
            <Button
              type="button"
              variant="secondary"
              onClick={() => void access.refetch()}
            >
              <RefreshCw size={14} aria-hidden="true" /> Retry
            </Button>
          </div>
        </div>
      ) : (
        <ul
          className={`${styles.peopleList} ${styles.peoplePanelList}`}
          aria-label="Administrators"
        >
          {administrators.map((administrator) => {
            const label = administratorLabel(administrator)
            return (
              <li key={administrator.targetUid}>
                <span className={styles.avatar} aria-hidden="true">
                  {initials(label)}
                </span>
                <span className={styles.personText}>
                  <strong>{label}</strong>
                  <small>
                    {administrator.targetUid === user?.uid ? 'You · ' : ''}
                    All clinics
                  </small>
                </span>
                <StatusBadge
                  tone={administrator.role === 'OWNER' ? 'info' : 'neutral'}
                >
                  {administrator.role === 'OWNER' ? 'Owner' : 'Administrator'}
                </StatusBadge>
              </li>
            )
          })}
          {pending.map((invitation) => (
            <li key={invitation.invitationId} data-invitation>
              <span className={styles.avatar} aria-hidden="true">
                <Mail size={15} />
              </span>
              <span className={styles.personText}>
                <strong>{invitation.email}</strong>
                <small>Invitation · {invitationStatus(invitation)}</small>
              </span>
              <span className={styles.rowActions}>
                {invitation.status === 'ACTIVE' ? (
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={resend.isPending || revoke.isPending}
                    aria-label={`Resend invitation to ${invitation.email}`}
                    onClick={() => resend.mutate(invitation.email)}
                  >
                    Resend
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  className={styles.removeButton}
                  disabled={resend.isPending || revoke.isPending}
                  aria-label={`${invitation.status === 'CLAIMING' ? 'Cancel acceptance' : 'Cancel invitation'} for ${invitation.email}`}
                  onClick={() => {
                    revoke.reset()
                    setCancelling(invitation)
                  }}
                >
                  Cancel
                </Button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {inviting && accessVerified ? (
        <AdministratorInviteDialog
          organizationName={organization.organization.name}
          onInvite={(email) =>
            api.createAdministratorInvitation!(user, { email })
          }
          onInvited={async (email) => {
            setInviting(false)
            notify?.('success', `Invitation sent to ${email}.`)
            await refresh()
          }}
          onClose={() => setInviting(false)}
        />
      ) : null}

      {cancelling && accessVerified ? (
        <SettingsConfirmDialog
          title={
            cancelling.status === 'CLAIMING'
              ? 'Cancel acceptance?'
              : 'Cancel invitation?'
          }
          confirmLabel={
            cancelling.status === 'CLAIMING'
              ? 'Cancel acceptance'
              : 'Cancel invitation'
          }
          cancelLabel="Keep invitation"
          busy={revoke.isPending}
          busyLabel="Cancelling…"
          error={revoke.isError ? cancelInvitationError(revoke.error) : null}
          danger
          onConfirm={() => revoke.mutate(cancelling)}
          onCancel={() => setCancelling(null)}
        >
          <p>
            {cancelling.status === 'CLAIMING'
              ? `Cancel the in-progress acceptance for ${cancelling.email}? This is allowed only after the active claim window has ended and no administrator access was granted.`
              : `Cancel the administrator invitation for ${cancelling.email}?`}
          </p>
        </SettingsConfirmDialog>
      ) : null}
    </section>
  )
}

function AdministratorInviteDialog({
  organizationName,
  onInvite,
  onInvited,
  onClose,
}: {
  organizationName: string
  onInvite: (email: string) => Promise<unknown>
  onInvited: (email: string) => Promise<void>
  onClose: () => void
}) {
  const dialog = useRef<HTMLElement>(null)
  const titleId = useId()
  const emailId = useId()
  const hintId = useId()
  const [email, setEmail] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const invite = useMutation({
    mutationFn: onInvite,
    onSuccess: (_result, sent) => onInvited(sent),
  })
  usePatientDialog(dialog, { onClose, locked: invite.isPending })

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const found = emailProblem(email)
    setProblem(found)
    if (!found) invite.mutate(email.trim())
  }

  const error =
    problem ?? (invite.isError ? invitationError(invite.error) : null)

  return createPortal(
    <div className={panel.backdrop} role="presentation">
      <section
        ref={dialog}
        className={`${panel.dialog} ${styles.inviteDialog}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <form onSubmit={submit} noValidate>
          <header className={panel.dialogHeader}>
            <div>
              <span className={panel.eyebrow}>Administrators</span>
              <h2 id={titleId}>Invite an administrator</h2>
              <p>
                They’ll have the same access as you to{' '}
                <strong>{organizationName}</strong> and all of its clinics.
              </p>
            </div>
            <button
              type="button"
              className={panel.closeButton}
              aria-label="Close"
              disabled={invite.isPending}
              onClick={onClose}
            >
              <X size={18} aria-hidden="true" />
            </button>
          </header>
          <div className={panel.dialogBody}>
            <label className={panel.field} htmlFor={emailId}>
              <span>Administrator email</span>
              <input
                id={emailId}
                type="email"
                autoComplete="email"
                data-autofocus
                value={email}
                readOnly={invite.isPending}
                aria-invalid={error ? true : undefined}
                aria-describedby={hintId}
                onChange={(event) => {
                  setEmail(event.target.value)
                  setProblem(null)
                  if (invite.isError) invite.reset()
                }}
              />
            </label>
            {error ? (
              <p id={hintId} className={panel.fieldError} role="alert">
                <AlertTriangle size={14} aria-hidden="true" />
                {error}
              </p>
            ) : (
              <p id={hintId} className={panel.hint}>
                They’ll get an email to accept. They choose their own password.
              </p>
            )}
          </div>
          <footer className={panel.dialogFooter}>
            <div className={panel.footerActions}>
              <Button
                type="button"
                variant="secondary"
                disabled={invite.isPending}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={invite.isPending}>
                <Send size={15} aria-hidden="true" />
                {invite.isPending ? 'Sending…' : 'Send invitation'}
              </Button>
            </div>
          </footer>
        </form>
      </section>
    </div>,
    document.body
  )
}
